import { Component, ErrorInfo, ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RouteGuard, setCredentials } from '@hs/shared';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { trpc } from '@/lib/trpc';
import { homeRoutes } from '@/routes';

/**
 * 错误边界：捕获渲染错误
 */
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>出错了</h1>
          <p>{this.state.error?.message}</p>
          <pre
            style={{
              textAlign: 'left',
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
            }}
          >
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Home 应用主组件
 * 
 * ✅ 受保护路由：RouteGuard 检查认证状态
 * ✅ 支持嵌套路由结构
 * ✅ 逻辑清晰，便于扩展
 */
function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  return (
    <ErrorBoundary>
      <Routes>
        {homeRoutes.map((route) => {
          // 处理嵌套路由
          if (route.children && route.children.length > 0) {
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <RouteGuard
                    meta={route.meta}
                    isAuthenticated={isAuthenticated}
                    trpc={trpc}
                    dispatch={dispatch}
                    setCredentials={setCredentials}
                    isHomeApp={true}
                  >
                    {route.element}
                  </RouteGuard>
                }
              >
                {route.children.map((child) => {
                  if (child.index) {
                    return (
                      <Route
                        key="index"
                        index
                        element={child.element}
                      />
                    );
                  }
                  return (
                    <Route
                      key={child.path}
                      path={child.path}
                      element={child.element}
                    />
                  );
                })}
              </Route>
            );
          }

          // 普通路由
          const { path, element, meta } = route;
          const requiresAuth = meta?.requiresAuth !== false;

          // ✅ home 应用：所有路由都经过 RouteGuard（用于检查 Cookie 并更新认证状态）
          // 但 requiresAuth: false 的路由不会强制跳转，允许未登录用户访问
          const guardedElement = (
            <RouteGuard
              meta={meta}
              isAuthenticated={isAuthenticated}
              trpc={trpc}
              dispatch={dispatch}
              setCredentials={setCredentials}
              isHomeApp={true}
            >
              {element}
            </RouteGuard>
          );

          return <Route key={path} path={path} element={guardedElement} />;
        })}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
